import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  History, ChevronDown, ChevronRight, Undo2, Loader2,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { useToast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * Payroll-Adjustments summary banner + drilldown (Feb 2026).
 *
 * Shown above the worker table on the Payroll Workbench. Aggregates every
 * edited time-entry in the current pay period so HR can:
 *   (1) see at a glance "X sessions normalized by Y admin" — paper-trail receipt
 *   (2) drill into each edit to compare original vs current hours, and
 *   (3) one-click revert any single edit back to its `original_clock_*`.
 *
 * Mounted as a child of PayrollWorkbench — receives the same pay-period
 * dates and an `onChange` callback that fires after a successful revert
 * so the parent can refetch its main report.
 */
const PayrollAdjustments = ({ token, startDate, endDate, onChange, refreshKey }) => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [reverting, setReverting] = useState({}); // entry_id -> true
  const [confirmRevert, setConfirmRevert] = useState(null); // entry

  const fetchAdjustments = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/v2/payroll-adjustments`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      // Soft-fail — banner just doesn't render. We deliberately do NOT toast
      // because supervisor-tier users may hit 403 here.
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Refetch on range or on outside changes (e.g. after parent triggers
  // a bulk edit). PayrollWorkbench wires `key={refreshKey}` indirectly
  // through the `onChange` callback below.
  useEffect(() => { fetchAdjustments(); /* eslint-disable-next-line */ }, [startDate, endDate, refreshKey]);

  const summary = useMemo(() => {
    if (!data) return null;
    return {
      total: data.total_edited || 0,
      added: data.total_hours_added || 0,
      removed: data.total_hours_removed || 0,
      admins: Object.entries(data.by_admin || {}),
      reasons: Object.entries(data.by_reason || {}),
    };
  }, [data]);

  const doRevert = async (entry) => {
    setReverting((p) => ({ ...p, [entry.entry_id]: true }));
    try {
      await axios.post(
        `${API}/time-entries/${entry.entry_id}/revert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: 'Edit reverted',
        description: `${entry.user_name} — restored to original ${entry.original_hours}h.`,
      });
      setConfirmRevert(null);
      await fetchAdjustments();
      onChange?.();
    } catch (e) {
      toast({
        title: 'Revert failed',
        description: e.response?.data?.detail || 'Could not revert this edit.',
        variant: 'destructive',
      });
    } finally {
      setReverting((p) => ({ ...p, [entry.entry_id]: false }));
    }
  };

  // Don't render the banner if there's nothing to show in the period.
  if (loading && !data) return null;
  if (!summary || summary.total === 0) return null;

  return (
    <div
      className="border border-blue-700/40 rounded-lg bg-blue-500/5 overflow-hidden"
      data-testid="pw-adjustments-banner"
    >
      {/* Header (always visible) */}
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-blue-500/10 transition-colors"
        data-testid="pw-adjustments-toggle"
      >
        <div className="flex items-center gap-3 text-sm">
          <History className="w-4 h-4 text-blue-300" />
          <span className="text-blue-100">
            <b data-testid="pw-adjustments-count">{summary.total}</b>{' '}
            session{summary.total === 1 ? '' : 's'} adjusted in this pay period
            {summary.admins.length > 0 && (
              <span className="text-blue-300/80"> · by {summary.admins.map(([n]) => n).join(', ')}</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-blue-200/80">
          {summary.added > 0 && <span data-testid="pw-adjustments-added">+{summary.added.toFixed(2)}h added</span>}
          {summary.removed > 0 && <span data-testid="pw-adjustments-removed">−{summary.removed.toFixed(2)}h removed</span>}
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
      </button>

      {/* Drilldown */}
      {expanded && (
        <div className="border-t border-blue-700/40 bg-[#0B1320] p-3 space-y-3" data-testid="pw-adjustments-drilldown">
          {/* By-reason breakdown */}
          {summary.reasons.length > 0 && (
            <div className="flex flex-wrap gap-2 text-xs">
              {summary.reasons.map(([tag, count]) => (
                <span
                  key={tag}
                  className="px-2 py-1 rounded border border-blue-700/40 bg-[#0F1923] text-blue-200"
                  data-testid={`pw-adjustments-reason-${tag}`}
                >
                  {tag}: <b>{count}</b>
                </span>
              ))}
            </div>
          )}

          {/* Entry list */}
          <div className="border border-[#1F2D3F] rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#0F1923]">
                <tr className="text-left" style={{ color: '#94A3B8' }}>
                  <th className="px-2 py-1.5">Worker</th>
                  <th className="px-2 py-1.5">Original</th>
                  <th className="px-2 py-1.5">→ Current</th>
                  <th className="px-2 py-1.5">Δ</th>
                  <th className="px-2 py-1.5">Edited by</th>
                  <th className="px-2 py-1.5">Reason</th>
                  <th className="px-2 py-1.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.entry_id} className="border-t border-[#1F2D3F]">
                    <td className="px-2 py-1.5 text-white">{e.user_name}</td>
                    <td className="px-2 py-1.5 text-slate-400">
                      {e.original_hours}h
                      <div className="text-[10px] opacity-60">{fmtTime(e.original_clock_in)} → {fmtTime(e.original_clock_out)}</div>
                    </td>
                    <td className="px-2 py-1.5 text-white">
                      {e.current_hours}h
                      <div className="text-[10px] opacity-60">{fmtTime(e.clock_in)} → {fmtTime(e.clock_out)}</div>
                    </td>
                    <td
                      className="px-2 py-1.5 font-semibold"
                      style={{ color: e.delta_hours > 0 ? '#FFA000' : e.delta_hours < 0 ? '#22C55E' : '#94A3B8' }}
                      data-testid={`pw-adjustments-delta-${e.entry_id}`}
                    >
                      {e.delta_hours > 0 ? `+${e.delta_hours}` : e.delta_hours}h
                    </td>
                    <td className="px-2 py-1.5 text-slate-300">
                      {e.edited_by_name}
                      <div className="text-[10px] opacity-60">{fmtTime(e.edited_at)}</div>
                    </td>
                    <td className="px-2 py-1.5 text-slate-300 max-w-[220px] truncate" title={e.edit_reason}>
                      <span className="text-[10px] uppercase opacity-70 mr-1">{e.reason_tag}</span>
                      {e.edit_reason?.replace(/^\[[^\]]+\]\s*/, '') || '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => setConfirmRevert(e)}
                        disabled={!!reverting[e.entry_id]}
                        data-testid={`pw-adjustments-revert-${e.entry_id}`}
                      >
                        {reverting[e.entry_id]
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <><Undo2 className="w-3 h-3 mr-1" /> Revert</>}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Revert confirm */}
      <Dialog open={!!confirmRevert} onOpenChange={(o) => !o && setConfirmRevert(null)}>
        <DialogContent className="max-w-md" data-testid="pw-revert-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Revert this edit?</DialogTitle>
            <DialogDescription>
              {confirmRevert?.user_name}&apos;s session will return to its original clock-in/out values. The edit-history flag will be cleared.
            </DialogDescription>
          </DialogHeader>
          {confirmRevert && (
            <div className="space-y-2 text-xs">
              <div className="p-2 rounded border border-[#1F2D3F] bg-[#0F1923]">
                <div className="text-slate-400">Original</div>
                <div className="text-white font-medium">{confirmRevert.original_hours}h</div>
                <div className="text-[11px] text-slate-500">
                  {fmtTime(confirmRevert.original_clock_in)} → {fmtTime(confirmRevert.original_clock_out)}
                </div>
              </div>
              <div className="p-2 rounded border border-amber-700/40 bg-amber-500/5">
                <div className="text-amber-300">Current (will be discarded)</div>
                <div className="text-white font-medium">{confirmRevert.current_hours}h</div>
                <div className="text-[11px] text-amber-200/70">{confirmRevert.edit_reason}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevert(null)} data-testid="pw-revert-cancel">Cancel</Button>
            <Button
              onClick={() => doRevert(confirmRevert)}
              disabled={!confirmRevert || reverting[confirmRevert?.entry_id]}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="pw-revert-confirm"
            >
              {reverting[confirmRevert?.entry_id]
                ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Reverting…</>
                : <><Undo2 className="w-3 h-3 mr-1" /> Revert to original</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollAdjustments;
